import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import type { Json } from '../types/database.types'

export interface UserSettingsAggregate {
  showRecoveryIndicator: boolean
  weekStartsMonday: boolean
  showDisciplineColors: boolean
  showWeatherIndicator: boolean
  showSleepIndicator: boolean
  showCalendarSummary: boolean
}

const DEFAULT_SETTINGS: UserSettingsAggregate = {
  showRecoveryIndicator: false,
  weekStartsMonday: true,
  showDisciplineColors: true,
  showWeatherIndicator: true,
  showSleepIndicator: true,
  showCalendarSummary: false,
}

const SETTINGS_KEY = 'calendar_settings'

export const useUserSettings = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const queryKey = ['user-settings', user?.id]

  const {
    data: settings = DEFAULT_SETTINGS,
    isLoading,
    error,
    refetch,
  } = useQuery<UserSettingsAggregate>({
    queryKey,
    enabled: Boolean(user?.id),
    initialData: DEFAULT_SETTINGS,
    queryFn: async () => {
      const { data, error: dbError } = await supabase
        .from('user_settings')
        .select('setting_value')
        .eq('user_id', user!.id)
        .eq('setting_key', SETTINGS_KEY)
        .maybeSingle()

      if (dbError) {
        throw dbError
      }

      const savedSettings = (data?.setting_value as Partial<UserSettingsAggregate> | null) ?? null
      return {
        ...DEFAULT_SETTINGS,
        ...(savedSettings || {}),
      }
    },
  })

  const saveSettings = useMutation({
    mutationFn: async (newSettings: UserSettingsAggregate) => {
      if (!user) throw new Error('User not authenticated')

      const { error: dbError } = await supabase.from('user_settings').upsert(
        {
          user_id: user.id,
          setting_key: SETTINGS_KEY,
          setting_value: newSettings as unknown as Json,
        },
        {
          onConflict: 'user_id,setting_key',
        }
      )

      if (dbError) {
        throw dbError
      }

      return newSettings
    },
    onMutate: async (newSettings) => {
      await queryClient.cancelQueries({ queryKey })
      const previousSettings = queryClient.getQueryData<UserSettingsAggregate>(queryKey)
      queryClient.setQueryData(queryKey, newSettings)
      return { previousSettings }
    },
    onError: (_err, _newSettings, context) => {
      if (context?.previousSettings) {
        queryClient.setQueryData(queryKey, context.previousSettings)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const updateSetting = useCallback(
    async (key: keyof UserSettingsAggregate, value: boolean) => {
      const newSettings = { ...settings, [key]: value }
      await saveSettings.mutateAsync(newSettings)
    },
    [settings, saveSettings]
  )

  const resetSettings = useCallback(async () => {
    await saveSettings.mutateAsync(DEFAULT_SETTINGS)
  }, [saveSettings])

  return {
    settings,
    isLoading,
    error: error ? (error as Error).message : null,
    updateSetting,
    resetSettings,
    reloadSettings: refetch,
  }
}
